import type { User } from '@datadog/browser-core';
import { v4 } from 'uuid';
import { ApolloError, GraphQLErrors, NetworkError } from '@apollo/client/errors';
import { Operation } from '@apollo/client';
import { ExecutionResult } from 'graphql';
import { ObjMap } from 'graphql/jsutils/ObjMap';
import { deferUntil } from 'src/utils/deferUntil';
import { AuthErrorMetadata } from 'src/experiences/OAuth/Error/useOAuthErrorState';
import { CallbackSchedulerTimeSlotWithoutStartTime } from 'src/experiences/CallbackScheduler/types';
import { ZHLPersonalizedNextStepState } from '../graphql/generated/graphql';
import { ZillowSession } from '../auth/types';
import { SessionErrorMetadata } from '../auth/types/RefreshTokenErrorMetadata';
import { FetchierConfig } from '../REST/fetchier/types';
import { datadog } from './init';
type CustomDatadogActionConfig = {
    /**
     * If set to true, the action will be deferred until the user is identified in datadog
     */
    deferUntilUserIsIdentified?: boolean;
    /**
     * If set to true, no matter how often you call the logging function, it will only execute the
     * first time for a page load
     */
    onlyLogOnce?: boolean;
};

/**
 * This is the list of pre-created facets in datadog. You should leverage them where possible instead
 * of creating new context values to allow for immediate aggregation of the data in datadog dashboards.
 *
 * If you need to introduce new facets, please create them in datadog and add them here
 */
type DatadogFacets = {
    /**
     * The location that the user requested before we redirected them to login
     */
    loginLocation: string | null;

    /**
     * The next destination for a user
     */
    nextDestination: string | null;

    /**
     * The selected timeslot for callback scheduler
     */
    selectedTimeSlot: CallbackSchedulerTimeSlotWithoutStartTime | null;

    /**
     * The loan ID of the loan loaded on a dashboard (may not necessarily be the currently selected loan
     * since the dashboard can be manually loaded with a different loan ID)
     */
    loanID: string | null;

    /**
     * @deprecated - use authError instead
     * The cause of a session error
     */
    cause: SessionErrorMetadata | null;

    /**
     * @deprecated - No longer used except for domain migration
     * Where a user was coming from
     */
    from: string | null;

    /**
     * @deprecated - No longer used except for domain migration
     * Where a user was redirected to
     */
    to: string | null;

    /**
     * @deprecated - use errorMessage or apolloError or authError or graphQLErrors instead
     * An arbitrary error
     */
    error: string | ApolloError | GraphQLErrors | null | AuthErrorMetadata;

    /**
     * @deprecated - use errorMessage or graphQLErrorsAsString instead
     * An arbitrary error
     */
    errorAsString: string | null;

    /**
     *
     */
    authError: AuthErrorMetadata | SessionErrorMetadata | null;

    /**
     * An apollo client error
     */
    apolloError: ApolloError | null;

    /**
     * The lead ID for the loan loaded on a dashboard
     */
    leadID: string | null;

    /**
     * The borrower ID for the user
     */
    borrower_id: string | null;

    /**
     * The current personalized next step stage for the user
     * @deprecated use personalizedNextStepStage
     */
    status: ZHLPersonalizedNextStepState | null;

    /**
     * @deprecated - use nextDestination instead
     * The landing url for a user who has just activated
     */
    landingUrl: string | null;

    /**
     * The current personalized next step stage for the user
     */
    personalizedNextStepStage: ZHLPersonalizedNextStepState | null;

    /**
     * The user's zillow session
     */
    session: ZillowSession | null;

    /**
     * The activation token for a failed activation
     */
    activationToken: string | null;

    /**
     * Any graphQL errors that may have occurred
     */
    graphQLErrorsAsString: string | null;

    /**
     * The GraphQL response that came in to the Apollo Client
     */
    response: ExecutionResult<ObjMap<unknown>, ObjMap<unknown>> | null;

    /**
     * The operation that was executed in Apollo Client
     */
    operation: Operation | null;

    /**
     * Any network error that occurred in Apollo Client
     */
    networkError: NetworkError | null;

    /**
     * Any graphQL errors that may have occurred
     */
    graphQLErrors: GraphQLErrors | null;

    /**
     * The error code for a graphQL error. Also used by Blend's error reporting
     */
    errorCode: string | null;

    /**
     * The error message for a given error. Also used by Blend's error reporting
     */
    errorMessage: string | null;

    /**
     * What our system considers the user's currently selected loan to be
     */
    currentlySelectedLoan: string | null;

    /**
     * The number of loans available to the user
     */
    numberOfLoans: number | null;

    /**
     * The lead source for the currently selected loan
     */
    leadSource: string | null;

    /**
     * The lead type for the currently selected loan
     */
    leadType: string | null;

    /**
     * The lead program for the currently selected loan
     */
    leadProgram: string | null;

    /**
     * The returned graphql data for a query or mutation
     */
    graphqlData: Record<string, unknown> | null;

    /**
     * Arbitrary data that can be passed to datadog
     */
    data: Record<string, unknown> | null;

    /**
     * @deprecated - use restRequest
     * The url a request was made to
     */
    url: RequestInfo | URL | null;

    /**
     * @deprecated - use restRequest
     * The request options
     */
    opts: FetchierConfig | null;

    /**
     * @deprecated - use restRequest
     * The request timeout
     */
    timeout: number | null;

    /**
     * The configuration for a rest request
     */
    restRequest: {
        url: RequestInfo | URL | null;
        opts: FetchierConfig | null;
        timeout: number | null;
    };
};

/**
 * Encapsulates all the behavior that we support with datadog, but wrapped with defer logic to defer loading datadog
 * until necessary to save on bundle size, and also to defer optionally defer logging telemetry until the user is identified
 */
export class DatadogClient {
    private readonly DATADOG_USER_SET_FLAG = 'homeloansdashboard.datadog.user-identified';
    private readonly DEFER_POLL_RATE_IN_MS = 100;

    private currentIdempotencyToken: string;

    private idempotentTelemetryRecord: Record<string, string>;

    private isUserIdentified = false;

    constructor() {
        this.idempotentTelemetryRecord = {};
        this.currentIdempotencyToken = v4();
    }

    public clearIdempotencyHistory() {
        this.currentIdempotencyToken = v4();
        this.idempotentTelemetryRecord = {};
    }

    async addAction<TContext extends DatadogFacets | undefined>(
        action: string,
        context?: Partial<TContext>,
        config: CustomDatadogActionConfig = {}
    ) {
        await this.performTelemetry(() => datadog.addAction(action, context), config, action);
    }
    async setUser(user: User) {
        datadog.setUser(user);
        this.isUserIdentified = true;
    }

    async addFeatureFlagEvaluation<TExperimentName extends string, TValue>(
        experimentName: TExperimentName,
        experimentValue: TValue
    ) {
        datadog.addFeatureFlagEvaluation(experimentName, experimentValue);
    }

    async addError<TError extends Error, TContext extends DatadogFacets | undefined>(
        error: TError,
        context?: Partial<TContext>,
        config: CustomDatadogActionConfig = {}
    ) {
        await this.performTelemetry(() => datadog.addError(error, context), config, error.name);
    }

    private async performTelemetry(logTelemetry: () => void, config: CustomDatadogActionConfig, actionOrError: string) {
        if (config.onlyLogOnce && this.idempotentTelemetryRecord[actionOrError] === this.currentIdempotencyToken) {
            return;
        }
        if (!config.deferUntilUserIsIdentified || this.isCurrentUserIdentified()) {
            this.registerIdempotentTelemetry(actionOrError);
            return logTelemetry();
        }
        await deferUntil(
            this.isCurrentUserIdentified.bind(this),
            this.performAndRegisterIdempotentTelemetry.bind(this, actionOrError, logTelemetry),
            this.DEFER_POLL_RATE_IN_MS
        );
    }

    private performAndRegisterIdempotentTelemetry(action: string, logTelemetry: () => void) {
        this.registerIdempotentTelemetry(action);
        logTelemetry();
    }

    private registerIdempotentTelemetry(action: string) {
        this.idempotentTelemetryRecord[action] = this.currentIdempotencyToken!;
    }

    private isCurrentUserIdentified() {
        return this.isUserIdentified;
    }
}

/**
 * A datadog client that allows for deferring the loading of the datadog client until it is needed, saving significantly
 * on the initial load time for the page since datadog is not a critical path dependency.
 */
export const client = new DatadogClient();
